/**
 * GERADO — NÃO EDITE À MÃO.
 *
 * Espelho de `dashboard/backend/services/planoCanonico.js`, que é a fonte
 * única do plano de contas. Para mudar qualquer coisa aqui, mude lá e rode:
 *
 *   cd dashboard/backend && npm run plano:sync
 *
 * `tests/planoCanonicoEspelho.test.js` falha enquanto os dois estiverem
 * diferentes.
 */

export const NATUREZAS = {
  "RECEITA": "receita",
  "DEDUCAO": "deducao",
  "CUSTO": "custo",
  "DESPESA": "despesa",
  "FINANCEIRO": "financeiro",
  "TRIBUTO_SOBRE_LUCRO": "tributoSobreLucro",
  "MOVIMENTO": "movimentoPatrimonial"
};

export const COMPORTAMENTOS = {
  "VARIAVEL": "variavel",
  "FIXO": "fixo",
  "SEMIVARIAVEL": "semivariavel",
  "NAO_SE_APLICA": "naoSeAplica"
};

export const PERFIS = [
  "essencial",
  "comercio",
  "servicos",
  "industria",
  "completo"
];

export const PREFIXOS_LEGADOS = [
  "1.1.",
  "1.2.",
  "2.1.1.1.",
  "2.1.1.2.",
  "2.1.1.3.",
  "2.1.1.4.",
  "2.1.1.5.",
  "3.2.2.",
  "3.2.3.1.",
  "3.2.3.2.",
  "3.2.3.3."
];

export const GRUPOS = [
  {
    "codigo": "1",
    "nome": "RECEITA OPERACIONAL BRUTA",
    "natureza": "receita",
    "pergunta": "Quanto a empresa faturou em cada mês?",
    "ajuda": "Some tudo que foi vendido no mês, antes de descontar imposto ou taxa. Se não separa por forma de recebimento, lance tudo numa linha só."
  },
  {
    "codigo": "2",
    "nome": "DEDUÇÕES DA RECEITA",
    "natureza": "deducao",
    "pergunta": "Quanto saiu da receita antes de sobrar alguma coisa?",
    "ajuda": "Imposto sobre a venda, devolução e desconto dado ao cliente. Não é custo: é receita que nunca foi sua."
  },
  {
    "codigo": "3",
    "nome": "CUSTO DO QUE FOI VENDIDO",
    "natureza": "custo",
    "pergunta": "Quanto custou o que você vendeu?",
    "ajuda": "O custo da mercadoria, do produto fabricado ou do serviço prestado — só do que SAIU no mês, não do que você comprou para o estoque."
  },
  {
    "codigo": "4",
    "nome": "DESPESAS OPERACIONAIS",
    "natureza": "despesa",
    "pergunta": "Quanto custou manter a empresa aberta?",
    "ajuda": "O que existe mesmo com venda zero: equipe, aluguel, contador, marketing, retirada dos sócios."
  },
  {
    "codigo": "5",
    "nome": "RESULTADO FINANCEIRO",
    "natureza": "financeiro",
    "pergunta": "Quanto o banco levou (ou devolveu)?",
    "ajuda": "Juros, tarifas e IOF de um lado; rendimento de aplicação do outro. A PARCELA do empréstimo não entra aqui — só os juros dela."
  },
  {
    "codigo": "6",
    "nome": "IR E CSLL",
    "natureza": "tributoSobreLucro",
    "pergunta": "Quanto de imposto sobre o lucro?",
    "ajuda": "Só quem apura pelo lucro real ou presumido preenche. No Simples Nacional o imposto já está nas deduções da receita."
  },
  {
    "codigo": "9",
    "nome": "MOVIMENTOS QUE NÃO SÃO RESULTADO",
    "natureza": "movimentoPatrimonial",
    "pergunta": "Que dinheiro entrou ou saiu sem ser lucro nem prejuízo?",
    "ajuda": "Compra de estoque, parcela de empréstimo, aporte dos sócios, compra de máquina. Mexe no caixa e NÃO mexe no resultado do mês."
  }
];

export const CONTAS = [
  {
    "codigo": "1.1.1",
    "nome": "Vendas à vista",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "receita",
    "comportamento": "naoSeAplica",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "1.1.1"
  },
  {
    "codigo": "1.1.2",
    "nome": "Vendas no cartão de débito",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "receita",
    "comportamento": "naoSeAplica",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos"
    ],
    "legado": "1.1.2"
  },
  {
    "codigo": "1.1.3",
    "nome": "Vendas no cartão de crédito",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "receita",
    "comportamento": "naoSeAplica",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos"
    ],
    "legado": "1.1.3"
  },
  {
    "codigo": "1.1.4",
    "nome": "Vendas no boleto / a prazo",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "receita",
    "comportamento": "naoSeAplica",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "1.1.5"
  },
  {
    "codigo": "1.1.5",
    "nome": "Vendas no PIX / transferência",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "receita",
    "comportamento": "naoSeAplica",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos"
    ],
    "legado": "1.1.6"
  },
  {
    "codigo": "1.1.6",
    "nome": "Vendas no marketplace / delivery",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "receita",
    "comportamento": "naoSeAplica",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio"
    ],
    "legado": "1.1.7"
  },
  {
    "codigo": "1.2.1",
    "nome": "Venda de produtos fabricados",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "receita",
    "comportamento": "naoSeAplica",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "industria"
    ],
    "legado": "1.1.10"
  },
  {
    "codigo": "1.2.2",
    "nome": "Venda de subprodutos e sucata",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "receita",
    "comportamento": "naoSeAplica",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "industria"
    ],
    "legado": "1.1.11"
  },
  {
    "codigo": "1.3.1",
    "nome": "Prestação de serviços",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "receita",
    "comportamento": "naoSeAplica",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "servicos",
      "industria"
    ],
    "legado": "1.1.20"
  },
  {
    "codigo": "1.3.2",
    "nome": "Contratos recorrentes / mensalidades",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "receita",
    "comportamento": "naoSeAplica",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "servicos"
    ],
    "legado": "1.1.21"
  },
  {
    "codigo": "1.4.1",
    "nome": "Frete cobrado do cliente",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "receita",
    "comportamento": "naoSeAplica",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "industria"
    ],
    "legado": "1.1.30"
  },
  {
    "codigo": "1.4.2",
    "nome": "Outras receitas operacionais",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "receita",
    "comportamento": "naoSeAplica",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "1.1.31"
  },
  {
    "codigo": "2.1.1",
    "nome": "Devoluções e cancelamentos de venda",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "deducao",
    "comportamento": "variavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "industria"
    ],
    "legado": "3.2.3.2.10"
  },
  {
    "codigo": "2.2.1",
    "nome": "Descontos concedidos ao cliente",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "deducao",
    "comportamento": "variavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "3.2.3.2.11"
  },
  {
    "codigo": "2.3.1",
    "nome": "Simples Nacional (DAS)",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "deducao",
    "comportamento": "variavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "3.2.3.2.1"
  },
  {
    "codigo": "2.3.2",
    "nome": "ICMS sobre vendas",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "deducao",
    "comportamento": "variavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "industria"
    ],
    "legado": "3.2.3.2.2"
  },
  {
    "codigo": "2.3.3",
    "nome": "ISS sobre serviços",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "deducao",
    "comportamento": "variavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "servicos"
    ],
    "legado": "3.2.3.2.3"
  },
  {
    "codigo": "2.3.4",
    "nome": "PIS sobre vendas",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "deducao",
    "comportamento": "variavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "3.2.3.2.4"
  },
  {
    "codigo": "2.3.5",
    "nome": "COFINS sobre vendas",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "deducao",
    "comportamento": "variavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "3.2.3.2.5"
  },
  {
    "codigo": "2.3.6",
    "nome": "IPI sobre vendas",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "deducao",
    "comportamento": "variavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "industria"
    ],
    "legado": "3.2.3.2.6"
  },
  {
    "codigo": "3.1.1",
    "nome": "CMV — custo da mercadoria vendida",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "custo",
    "comportamento": "variavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio"
    ],
    "legado": "3.2.2.1"
  },
  {
    "codigo": "3.1.2",
    "nome": "Frete sobre compras (que entrou no custo)",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "custo",
    "comportamento": "variavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "industria"
    ],
    "legado": "3.2.2.2"
  },
  {
    "codigo": "3.1.3",
    "nome": "Perdas e quebras de estoque",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "custo",
    "comportamento": "variavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio"
    ],
    "legado": "3.2.2.3"
  },
  {
    "codigo": "3.2.1.1",
    "nome": "Matéria-prima consumida",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "custo",
    "comportamento": "variavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "industria"
    ],
    "legado": "3.2.2.10"
  },
  {
    "codigo": "3.2.1.2",
    "nome": "Material de embalagem",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "custo",
    "comportamento": "variavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "industria"
    ],
    "legado": "3.2.2.11"
  },
  {
    "codigo": "3.2.1.3",
    "nome": "Material secundário e insumos",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "custo",
    "comportamento": "variavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "industria"
    ],
    "legado": "3.2.2.12"
  },
  {
    "codigo": "3.2.2.1",
    "nome": "Salários da produção (mão de obra direta)",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "custo",
    "comportamento": "semivariavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "industria"
    ],
    "legado": "3.2.2.30"
  },
  {
    "codigo": "3.2.2.2",
    "nome": "Encargos da mão de obra direta",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "custo",
    "comportamento": "semivariavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "industria"
    ],
    "legado": "3.2.2.31"
  },
  {
    "codigo": "3.2.3.1",
    "nome": "Energia elétrica da fábrica",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "custo",
    "comportamento": "semivariavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "industria"
    ],
    "legado": "3.2.2.32"
  },
  {
    "codigo": "3.2.3.2",
    "nome": "Manutenção de máquinas da produção",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "custo",
    "comportamento": "semivariavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "industria"
    ],
    "legado": "3.2.2.33"
  },
  {
    "codigo": "3.2.3.3",
    "nome": "Mão de obra indireta da fábrica",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "custo",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "industria"
    ],
    "legado": "3.2.2.34"
  },
  {
    "codigo": "3.2.3.4",
    "nome": "Depreciação de máquinas e instalações da fábrica",
    "refRFB": null,
    "ehDepreciacao": true,
    "natureza": "custo",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": false,
    "perfis": [
      "industria"
    ],
    "legado": "3.2.2.35"
  },
  {
    "codigo": "3.3.1",
    "nome": "Mão de obra aplicada no serviço",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "custo",
    "comportamento": "semivariavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "servicos"
    ],
    "legado": "3.2.2.20"
  },
  {
    "codigo": "3.3.2",
    "nome": "Material aplicado no serviço",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "custo",
    "comportamento": "variavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "servicos"
    ],
    "legado": "3.2.2.21"
  },
  {
    "codigo": "3.3.3",
    "nome": "Serviços de terceiros aplicados no cliente",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "custo",
    "comportamento": "variavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "servicos"
    ],
    "legado": "3.2.2.22"
  },
  {
    "codigo": "4.1.1",
    "nome": "Comissão de vendedores",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "variavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "3.2.3.1.1"
  },
  {
    "codigo": "4.1.2",
    "nome": "Taxa da maquininha / operadora de cartão",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "variavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos"
    ],
    "legado": "3.2.3.3.1"
  },
  {
    "codigo": "4.1.3",
    "nome": "Taxa de marketplace / aplicativo de delivery",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "variavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio"
    ],
    "legado": "3.2.3.3.2"
  },
  {
    "codigo": "4.1.4",
    "nome": "Frete sobre vendas (entrega ao cliente)",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "variavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "industria"
    ],
    "legado": "3.2.3.3.3"
  },
  {
    "codigo": "4.1.5",
    "nome": "Agência de marketing",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.5.1"
  },
  {
    "codigo": "4.1.6",
    "nome": "Impulsionamento e tráfego pago",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "semivariavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.5.4"
  },
  {
    "codigo": "4.1.7",
    "nome": "Brindes, feiras e material promocional",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "industria"
    ],
    "legado": "2.1.1.5.5"
  },
  {
    "codigo": "4.1.8",
    "nome": "Viagens e visitas a clientes",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.5.6"
  },
  {
    "codigo": "4.2.1",
    "nome": "Aluguel",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.2"
  },
  {
    "codigo": "4.2.2",
    "nome": "Condomínio",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos"
    ],
    "legado": "2.1.1.3.7"
  },
  {
    "codigo": "4.2.3",
    "nome": "IPTU e taxas do imóvel",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.24"
  },
  {
    "codigo": "4.2.4",
    "nome": "Energia elétrica",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "semivariavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.16"
  },
  {
    "codigo": "4.2.5",
    "nome": "Água e esgoto",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.1"
  },
  {
    "codigo": "4.2.6",
    "nome": "Gás",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "industria"
    ],
    "legado": "2.1.1.3.20"
  },
  {
    "codigo": "4.2.7",
    "nome": "Internet",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.23"
  },
  {
    "codigo": "4.2.8",
    "nome": "Telefone",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.38"
  },
  {
    "codigo": "4.2.9",
    "nome": "Contabilidade",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.9"
  },
  {
    "codigo": "4.2.10",
    "nome": "Honorários advocatícios e consultoria",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.22"
  },
  {
    "codigo": "4.2.11",
    "nome": "Mensalidade de sistema / software",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.33"
  },
  {
    "codigo": "4.2.12",
    "nome": "Material de escritório",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.29"
  },
  {
    "codigo": "4.2.13",
    "nome": "Material de copa e cozinha",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.30"
  },
  {
    "codigo": "4.2.14",
    "nome": "Material de higiene e limpeza",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.31"
  },
  {
    "codigo": "4.2.15",
    "nome": "Manutenção do prédio e das instalações",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.26"
  },
  {
    "codigo": "4.2.16",
    "nome": "Manutenção de máquinas e equipamentos",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.27"
  },
  {
    "codigo": "4.2.17",
    "nome": "Combustível e transporte",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "semivariavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.21"
  },
  {
    "codigo": "4.2.18",
    "nome": "Manutenção de veículos",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.50"
  },
  {
    "codigo": "4.2.19",
    "nome": "Seguros",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.51"
  },
  {
    "codigo": "4.2.20",
    "nome": "Alvarás, licenças e taxas públicas",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.52"
  },
  {
    "codigo": "4.2.21",
    "nome": "Dedetização e controle de pragas",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "industria"
    ],
    "legado": "2.1.1.3.13"
  },
  {
    "codigo": "4.2.22",
    "nome": "Segurança e monitoramento",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "industria"
    ],
    "legado": "2.1.1.3.53"
  },
  {
    "codigo": "4.2.23",
    "nome": "Uniformes e EPI",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "industria"
    ],
    "legado": "2.1.1.3.54"
  },
  {
    "codigo": "4.2.24",
    "nome": "Treinamento e cursos",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.55"
  },
  {
    "codigo": "4.2.25",
    "nome": "Aluguel de máquina de cartão",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos"
    ],
    "legado": "2.1.1.3.3"
  },
  {
    "codigo": "4.2.26",
    "nome": "Outras despesas administrativas",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.56"
  },
  {
    "codigo": "4.3.1",
    "nome": "Salários",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.1.12"
  },
  {
    "codigo": "4.3.2",
    "nome": "FGTS",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.1.6"
  },
  {
    "codigo": "4.3.3",
    "nome": "INSS / GPS",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.1.8"
  },
  {
    "codigo": "4.3.4",
    "nome": "Férias",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.1.5"
  },
  {
    "codigo": "4.3.5",
    "nome": "13º salário",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.1.4"
  },
  {
    "codigo": "4.3.6",
    "nome": "Provisões de férias e 13º",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": false,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.1.10"
  },
  {
    "codigo": "4.3.7",
    "nome": "Rescisões",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.1.11"
  },
  {
    "codigo": "4.3.8",
    "nome": "Vale transporte",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.1.17"
  },
  {
    "codigo": "4.3.9",
    "nome": "Vale alimentação / refeição",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.1.16"
  },
  {
    "codigo": "4.3.10",
    "nome": "Plano de saúde",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.1.20"
  },
  {
    "codigo": "4.3.11",
    "nome": "Estagiários e aprendizes",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.1.21"
  },
  {
    "codigo": "4.3.12",
    "nome": "Serviços de terceiros (PJ) da administração",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.1.22"
  },
  {
    "codigo": "4.4.1",
    "nome": "Pró-labore",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.2.1"
  },
  {
    "codigo": "4.4.2",
    "nome": "INSS sobre pró-labore",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.2.2"
  },
  {
    "codigo": "4.4.3",
    "nome": "Plano de saúde dos sócios",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.2.3"
  },
  {
    "codigo": "4.4.4",
    "nome": "Despesas pessoais pagas pela empresa",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.2.4"
  },
  {
    "codigo": "4.5.1",
    "nome": "Depreciação de máquinas e equipamentos",
    "refRFB": null,
    "ehDepreciacao": true,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": false,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.60"
  },
  {
    "codigo": "4.5.2",
    "nome": "Depreciação de veículos",
    "refRFB": null,
    "ehDepreciacao": true,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": false,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.61"
  },
  {
    "codigo": "4.5.3",
    "nome": "Depreciação de móveis e instalações",
    "refRFB": null,
    "ehDepreciacao": true,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": false,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.62"
  },
  {
    "codigo": "4.5.4",
    "nome": "Amortização de intangível",
    "refRFB": null,
    "ehDepreciacao": true,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": false,
    "perfis": [
      "completo"
    ],
    "legado": "2.1.1.3.63"
  },
  {
    "codigo": "4.6.1",
    "nome": "Perdas com clientes inadimplentes",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.64"
  },
  {
    "codigo": "4.6.2",
    "nome": "Multas e infrações",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.3.65"
  },
  {
    "codigo": "4.6.3",
    "nome": "Doações e patrocínios",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "despesa",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "completo"
    ],
    "legado": "2.1.1.3.66"
  },
  {
    "codigo": "5.1.1",
    "nome": "Rendimento de aplicações financeiras",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "receita",
    "comportamento": "naoSeAplica",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "1.2.10"
  },
  {
    "codigo": "5.1.2",
    "nome": "Juros recebidos de clientes",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "receita",
    "comportamento": "naoSeAplica",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "1.2.11"
  },
  {
    "codigo": "5.2.1",
    "nome": "Juros de empréstimos e financiamentos",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "financeiro",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.4.4"
  },
  {
    "codigo": "5.2.2",
    "nome": "Juros de cheque especial",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "financeiro",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.4.2"
  },
  {
    "codigo": "5.2.3",
    "nome": "Juros pagos a fornecedores",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "financeiro",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.4.7"
  },
  {
    "codigo": "5.2.4",
    "nome": "Juros e multas de mora",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "financeiro",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.4.6"
  },
  {
    "codigo": "5.2.5",
    "nome": "Tarifas bancárias",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "financeiro",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.4.9"
  },
  {
    "codigo": "5.2.6",
    "nome": "Manutenção de conta bancária",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "financeiro",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.4.8"
  },
  {
    "codigo": "5.2.7",
    "nome": "IOF",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "financeiro",
    "comportamento": "fixo",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.4.11"
  },
  {
    "codigo": "5.2.8",
    "nome": "Encargos bancários e antecipação de recebíveis",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "financeiro",
    "comportamento": "semivariavel",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.4.5"
  },
  {
    "codigo": "6.1.1",
    "nome": "IRPJ",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "tributoSobreLucro",
    "comportamento": "naoSeAplica",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "completo"
    ],
    "legado": null
  },
  {
    "codigo": "6.1.2",
    "nome": "CSLL",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "tributoSobreLucro",
    "comportamento": "naoSeAplica",
    "afetaResultado": true,
    "afetaCaixa": true,
    "perfis": [
      "completo"
    ],
    "legado": null
  },
  {
    "codigo": "9.1.1",
    "nome": "Aporte de capital dos sócios",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "movimentoPatrimonial",
    "comportamento": "naoSeAplica",
    "afetaResultado": false,
    "afetaCaixa": true,
    "perfis": [
      "completo"
    ],
    "legado": "1.2.1"
  },
  {
    "codigo": "9.2.1",
    "nome": "Empréstimo captado",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "movimentoPatrimonial",
    "comportamento": "naoSeAplica",
    "afetaResultado": false,
    "afetaCaixa": true,
    "perfis": [
      "completo"
    ],
    "legado": "1.2.2"
  },
  {
    "codigo": "9.2.2",
    "nome": "Resgate de aplicações",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "movimentoPatrimonial",
    "comportamento": "naoSeAplica",
    "afetaResultado": false,
    "afetaCaixa": true,
    "perfis": [
      "completo"
    ],
    "legado": "1.2.3"
  },
  {
    "codigo": "9.3.1",
    "nome": "Parcela de empréstimo — principal",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "movimentoPatrimonial",
    "comportamento": "naoSeAplica",
    "afetaResultado": false,
    "afetaCaixa": true,
    "perfis": [
      "essencial",
      "comercio",
      "servicos",
      "industria"
    ],
    "legado": "2.1.1.4.3"
  },
  {
    "codigo": "9.3.2",
    "nome": "Uso de cheque especial — principal",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "movimentoPatrimonial",
    "comportamento": "naoSeAplica",
    "afetaResultado": false,
    "afetaCaixa": true,
    "perfis": [
      "completo"
    ],
    "legado": "2.1.1.4.1"
  },
  {
    "codigo": "9.4.1",
    "nome": "Distribuição de lucros aos sócios",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "movimentoPatrimonial",
    "comportamento": "naoSeAplica",
    "afetaResultado": false,
    "afetaCaixa": true,
    "perfis": [
      "completo"
    ],
    "legado": null
  },
  {
    "codigo": "9.5.1",
    "nome": "Compra de máquinas, veículos e imóveis",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "movimentoPatrimonial",
    "comportamento": "naoSeAplica",
    "afetaResultado": false,
    "afetaCaixa": true,
    "perfis": [
      "completo"
    ],
    "legado": null
  },
  {
    "codigo": "9.6.1",
    "nome": "Compra de mercadoria para estoque",
    "refRFB": null,
    "ehDepreciacao": false,
    "natureza": "movimentoPatrimonial",
    "comportamento": "naoSeAplica",
    "afetaResultado": false,
    "afetaCaixa": true,
    "perfis": [
      "comercio",
      "industria"
    ],
    "legado": "3.2.2.50"
  }
];

export const TAGS = {
  "REVENDE": "revende",
  "FABRICA": "fabrica",
  "SERVICO": "servico",
  "CARTAO": "cartao",
  "PRAZO": "prazo",
  "MARKETPLACE": "marketplace",
  "TEM_FUNCIONARIO": "temFuncionario",
  "TEM_DIVIDA": "temDivida",
  "IMPOSTO_UNICO": "impostoUnico",
  "IMPOSTO_SEPARADO": "impostoSeparado",
  "DETALHADO": "detalhado"
};

export const QUANDO = {
  "1.1.1": [],
  "1.1.2": [
    "cartao"
  ],
  "1.1.3": [
    "cartao"
  ],
  "1.1.4": [
    "prazo"
  ],
  "1.1.5": [],
  "1.1.6": [
    "marketplace"
  ],
  "1.2.1": [
    "fabrica"
  ],
  "1.2.2": [
    "fabrica"
  ],
  "1.3.1": [
    "servico"
  ],
  "1.3.2": [
    "servico"
  ],
  "1.4.1": [
    "revende",
    "fabrica"
  ],
  "1.4.2": [
    "detalhado"
  ],
  "2.1.1": [
    "revende",
    "fabrica"
  ],
  "2.2.1": [
    "detalhado"
  ],
  "2.3.1": [
    "impostoUnico"
  ],
  "2.3.2": [
    "impostoSeparado"
  ],
  "2.3.3": [
    "impostoSeparado"
  ],
  "2.3.4": [
    "impostoSeparado"
  ],
  "2.3.5": [
    "impostoSeparado"
  ],
  "2.3.6": [
    "impostoSeparado"
  ],
  "3.1.1": [
    "revende"
  ],
  "3.1.2": [
    "revende",
    "fabrica"
  ],
  "3.1.3": [
    "revende"
  ],
  "3.2.1.1": [
    "fabrica"
  ],
  "3.2.1.2": [
    "fabrica"
  ],
  "3.2.1.3": [
    "fabrica"
  ],
  "3.2.2.1": [
    "fabrica"
  ],
  "3.2.2.2": [
    "fabrica"
  ],
  "3.2.3.1": [
    "fabrica"
  ],
  "3.2.3.2": [
    "fabrica"
  ],
  "3.2.3.3": [
    "fabrica"
  ],
  "3.2.3.4": [
    "fabrica"
  ],
  "3.3.1": [
    "servico"
  ],
  "3.3.2": [
    "servico"
  ],
  "3.3.3": [
    "servico"
  ],
  "4.1.1": [],
  "4.1.2": [
    "cartao"
  ],
  "4.1.3": [
    "marketplace"
  ],
  "4.1.4": [
    "revende",
    "fabrica"
  ],
  "4.1.5": [],
  "4.1.6": [],
  "4.1.7": [
    "revende",
    "fabrica"
  ],
  "4.1.8": [
    "detalhado"
  ],
  "4.2.1": [],
  "4.2.2": [],
  "4.2.3": [
    "detalhado"
  ],
  "4.2.4": [],
  "4.2.5": [],
  "4.2.6": [
    "revende",
    "fabrica"
  ],
  "4.2.7": [],
  "4.2.8": [],
  "4.2.9": [],
  "4.2.10": [
    "detalhado"
  ],
  "4.2.11": [],
  "4.2.12": [],
  "4.2.13": [
    "detalhado"
  ],
  "4.2.14": [],
  "4.2.15": [
    "detalhado"
  ],
  "4.2.16": [],
  "4.2.17": [],
  "4.2.18": [
    "detalhado"
  ],
  "4.2.19": [
    "detalhado"
  ],
  "4.2.20": [
    "detalhado"
  ],
  "4.2.21": [
    "revende",
    "fabrica"
  ],
  "4.2.22": [
    "revende",
    "fabrica"
  ],
  "4.2.23": [
    "revende",
    "fabrica"
  ],
  "4.2.24": [
    "detalhado"
  ],
  "4.2.25": [
    "detalhado"
  ],
  "4.2.26": [
    "detalhado"
  ],
  "4.3.1": [
    "temFuncionario"
  ],
  "4.3.2": [
    "temFuncionario"
  ],
  "4.3.3": [
    "temFuncionario"
  ],
  "4.3.4": [
    "temFuncionario"
  ],
  "4.3.5": [
    "temFuncionario"
  ],
  "4.3.6": [
    "temFuncionario"
  ],
  "4.3.7": [
    "temFuncionario"
  ],
  "4.3.8": [
    "temFuncionario"
  ],
  "4.3.9": [
    "temFuncionario"
  ],
  "4.3.10": [
    "temFuncionario"
  ],
  "4.3.11": [
    "temFuncionario"
  ],
  "4.3.12": [
    "temFuncionario"
  ],
  "4.4.1": [],
  "4.4.2": [
    "detalhado"
  ],
  "4.4.3": [
    "detalhado"
  ],
  "4.4.4": [
    "detalhado"
  ],
  "4.5.1": [
    "detalhado"
  ],
  "4.5.2": [
    "detalhado"
  ],
  "4.5.3": [
    "detalhado"
  ],
  "4.5.4": [
    "detalhado"
  ],
  "4.6.1": [
    "detalhado"
  ],
  "4.6.2": [
    "detalhado"
  ],
  "4.6.3": [
    "detalhado"
  ],
  "5.1.1": [
    "detalhado"
  ],
  "5.1.2": [
    "detalhado"
  ],
  "5.2.1": [
    "temDivida"
  ],
  "5.2.2": [
    "temDivida"
  ],
  "5.2.3": [
    "temDivida"
  ],
  "5.2.4": [
    "temDivida"
  ],
  "5.2.5": [],
  "5.2.6": [],
  "5.2.7": [
    "temDivida"
  ],
  "5.2.8": [
    "temDivida"
  ],
  "6.1.1": [
    "impostoSeparado"
  ],
  "6.1.2": [
    "impostoSeparado"
  ],
  "9.1.1": [
    "detalhado"
  ],
  "9.2.1": [
    "detalhado"
  ],
  "9.2.2": [
    "detalhado"
  ],
  "9.3.1": [
    "temDivida"
  ],
  "9.3.2": [
    "temDivida"
  ],
  "9.4.1": [
    "detalhado"
  ],
  "9.5.1": [
    "detalhado"
  ],
  "9.6.1": [
    "revende",
    "fabrica"
  ]
};
